<app-spinner-overlay [showSpinner]="isEditingBooking"></app-spinner-overlay>

<div class="vstack gap-3 fs-5">
    <header class="d-flex align-items-center justify-content-between">
        <h5 class="fw-semibold m-0">Payments</h5>
        @if (noPaymentsInvolved || canAddAnotherDepositCharge()) {
            <div ngbDropdown placement="bottom-end">
                <button ngbDropdownToggle class="btn btn-outline-secondary compact" type="button" aria-label="Payment actions">
                    <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
                </button>
                <div ngbDropdownMenu>
                    <button ngbDropdownItem (click)="addDepositCharge()">Add Deposit Charge</button>
                    @if (noPaymentsInvolved) {
                        <button ngbDropdownItem [disabled]="!canRequireCardDetails" (click)="addCancellationCharge()">Add Cancellation Charge</button>
                    }
                </div>
            </div>
        }
    </header>
    <div class="vstack gap-1 border-bottom pb-3" data-testid="booking-payment-summary">
        <div class="d-flex align-items-baseline gap-2">
            <strong class="payment-total">{{ null | currencyUnit:paymentSummary.balance:paymentSummary.currencyCode }}</strong>
            <span class="fw-semibold">{{ paymentSummary.amountRefunded > 0 ? 'Paid after refunds' : 'Paid' }}</span>
        </div>
        <small class="text-body-secondary">
            @if (paymentSummary.amountPaid > 0) {
                {{ null | currencyUnit:paymentSummary.amountPaid:paymentSummary.currencyCode }} paid
                @if (paymentSummary.amountRefunded > 0) {
                    · {{ null | currencyUnit:paymentSummary.amountRefunded:paymentSummary.currencyCode }} refunded
                }
            } @else { No payments yet }
        </small>
    </div>

    <div ngbAccordion class="vstack gap-3">
        @if (booking.order; as order) {
            <section ngbAccordionItem #products="ngbAccordionItem" [collapsed]="false" class="border rounded-3 overflow-hidden bg-body" data-testid="booking-order-payment" aria-label="Products">
                <h6 ngbAccordionHeader class="m-0">
                    <button ngbAccordionToggle type="button" class="btn border-0 rounded-0 text-start p-3 d-flex align-items-center gap-3 w-100" data-testid="products-toggle">
                        <i class="bi" [class.bi-chevron-right]="products.collapsed" [class.bi-chevron-down]="!products.collapsed" aria-hidden="true"></i>
                        <span class="fw-semibold fs-6">Products</span>
                        @if (order.isPaid) {
                            <strong class="ms-auto text-nowrap" [class.text-danger]="!booking.sale?.payment?.datePaid" data-testid="products-balance">
                                @if (!booking.sale?.payment?.datePaid) { {{ booking.canPayForOrder() || booking.statusType === BookingStatusType.PendingPayment ? 'Due' : 'Unpaid' }} }
                                {{ null | currencyUnit:(booking.sale?.payment?.datePaid ? booking.sale!.payment!.balance : order.total()):order.currencyCode }}
                            </strong>
                        }
                    </button>
                </h6>
                <div ngbAccordionCollapse>
                    <div ngbAccordionBody class="p-3 border-top">
                        <ng-template>
                            <div class="vstack gap-3">
                                @for (line of order.lineItems; track line.id) {
                                    <div class="d-flex justify-content-between gap-3" data-testid="order-payment-line" [class.border-top]="!$first" [class.pt-3]="!$first">
                                        <div>
                                            <span class="fw-semibold">{{ line.quantity }} × {{ line.name }}</span>
                                            @for (modifier of line.modifiers; track modifier.id) { <small class="d-block text-body-secondary">{{ modifier.name }}</small> }
                                            @if (order.isPaid && line.paidOnline) { <small class="d-block text-body-secondary">{{ null | currencyUnit:line.unitAmount():order.currencyCode }} each</small> }
                                        </div>
                                        @if (order.isPaid && line.paidOnline) {
                                            <div class="text-end text-nowrap">
                                                @if ((booking.sale?.payment?.amountRefundedForLine(line.id) ?? 0) > 0) {
                                                    <s class="text-body-secondary">{{ null | currencyUnit:line.unitAmount() * line.quantity:order.currencyCode }}</s>
                                                    <span class="text-danger ms-2">{{ null | currencyUnit:booking.sale!.remainingLineAmount(line.id):order.currencyCode }}</span>
                                                } @else {
                                                    {{ null | currencyUnit:line.unitAmount() * line.quantity:order.currencyCode }}
                                                }
                                            </div>
                                        }
                                    </div>
                                }
                                @if (booking.sale?.payment?.datePaid && booking.sale!.payment!.balance > 0) {
                                    <button class="btn btn-outline-secondary align-self-start" type="button" (click)="refundProducts()">{{ booking.sale!.payment!.canRefundOnline ? 'Refund Products' : 'Mark Products as Refunded' }}</button>
                                }
                            </div>
                        </ng-template>
                    </div>
                </div>
            </section>
        }

        @if (booking.deposit || booking.pendingDepositCharge) {
            <section ngbAccordionItem #deposit="ngbAccordionItem" [collapsed]="!booking.pendingDepositCharge" class="border rounded-3 overflow-hidden bg-body" data-testid="deposit-payment">
                <div ngbAccordionHeader class="d-flex align-items-center flex-wrap">
                    <button ngbAccordionToggle type="button" class="btn border-0 rounded-0 text-start p-3 d-flex align-items-center gap-3 flex-grow-1" data-testid="deposit-toggle">
                        <i class="bi" [class.bi-chevron-right]="deposit.collapsed" [class.bi-chevron-down]="!deposit.collapsed" aria-hidden="true"></i>
                        <span class="fw-semibold fs-6">Deposit</span>
                        @if (!booking.pendingDepositCharge) {
                            <span class="badge lh-sm" [class.bg-danger-subtle]="booking.deposit?.isRefunded()" [class.text-danger-emphasis]="booking.deposit?.isRefunded()"
                                [class.bg-success-subtle]="!booking.deposit?.isRefunded()" [class.text-success-emphasis]="!booking.deposit?.isRefunded()">{{ booking.deposit?.isRefunded() ? 'Refunded' : 'Paid' }}</span>
                        }
                        <strong class="ms-auto text-nowrap" [class.text-danger]="booking.pendingDepositCharge" data-testid="deposit-balance">
                            @if (booking.pendingDepositCharge; as pending) {
                                Due {{ null | currencyUnit:pending.amountForPartySize(booking.size):paymentSummary.currencyCode }}
                            } @else { {{ null | currencyUnit:(booking.deposit?.amountHeld() ?? 0):paymentSummary.currencyCode }} }
                        </strong>
                    </button>
                    @if (refundableDepositPayments.length === 1) {
                        <button type="button" class="btn btn-outline-secondary m-2 ms-auto" [disabled]="isRefundingPayment" (click)="refundPaymentClicked(refundableDepositPayments[0])">
                            {{ refundableDepositPayments[0].paymentIntentId ? 'Refund deposit' : 'Mark as refunded' }}
                        </button>
                    }
                </div>
                <div ngbAccordionCollapse>
                    <div ngbAccordionBody class="p-3 border-top">
                        <ng-template>
                            <div class="vstack gap-3">
                                @if (booking.deposit; as paidDeposit) {
                                    <div class="d-flex justify-content-between gap-3"><span>Deposit paid</span><span>{{ null | currencyUnit:paidDeposit.amountPaid():paidDeposit.currencyCode }}</span></div>
                                    @if (paidDeposit.amountRefunded() > 0) {
                                        <div class="d-flex justify-content-between gap-3 text-danger"><span>Refunded</span><span>{{ null | currencyUnit:paidDeposit.amountRefunded():paidDeposit.currencyCode }}</span></div>
                                    }
                                }
                                @if (refundableDepositPayments.length > 1) {
                                    @for (payment of refundableDepositPayments; track payment.id) {
                                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                            <span>{{ payment.paymentIntentId ? 'Online' : 'In person' }} · {{ payment.dateCreated | dateTime:venue.timeZone:DateTime.DATETIME_SHORT }} · {{ null | currencyUnit:payment.amountRefundable():payment.currencyCode }} remaining</span>
                                            <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="isRefundingPayment" (click)="refundPaymentClicked(payment)">{{ payment.paymentIntentId ? 'Refund' : 'Mark as refunded' }}</button>
                                        </div>
                                    }
                                }
                                <small class="text-body-secondary">
                                    @if (venue.depositRefundCutOffDate(booking); as deadline) { Refundable if cancelled before {{ deadline | dateTime:venue.timeZone:DateTime.DATETIME_SHORT }} }
                                    @else { Non-refundable to the customer }
                                </small>
                                @if (booking.pendingDepositCharge; as pending) {
                                    @if (pending.isPerCover) { <small class="text-body-secondary">{{ null | currencyUnit:pending.unitAmount:paymentSummary.currencyCode }} × {{ booking.size }} people</small> }
                                    <ng-container [ngTemplateOutlet]="pendingPaymentLink" [ngTemplateOutletContext]="{ card: false }"></ng-container>
                                    <div class="d-flex flex-wrap gap-2">
                                        <button type="button" class="btn btn-sm btn-outline-secondary" (click)="editPendingPayment()">Edit Deposit Charge</button>
                                        @if (booking.status().canWaivePendingPayment()) { <button type="button" class="btn btn-sm btn-outline-secondary" (click)="waivePendingPayment()">Waive Pending Payment</button> }
                                        @if (booking.status().canManuallyMarkAsPaid()) { <button type="button" class="btn btn-sm btn-outline-secondary" (click)="markAsManuallyPaid()">Mark as Paid in Person</button> }
                                    </div>
                                }
                            </div>
                        </ng-template>
                    </div>
                </div>
            </section>
        }

        @if (booking.cancellation || booking.pendingCancellationCharge) {
            <section ngbAccordionItem #cancellation="ngbAccordionItem" [collapsed]="!booking.pendingCancellationCharge" class="border rounded-3 overflow-hidden bg-body" data-testid="cancellation-payment">
                <h6 ngbAccordionHeader class="m-0">
                    <button ngbAccordionToggle type="button" class="btn border-0 rounded-0 text-start p-3 d-flex align-items-center flex-wrap gap-2 w-100" data-testid="cancellation-toggle">
                        <i class="bi" [class.bi-chevron-right]="cancellation.collapsed" [class.bi-chevron-down]="!cancellation.collapsed" aria-hidden="true"></i>
                        <span class="fw-semibold fs-6">Cancellation charge</span>
                        <small class="text-body-secondary ms-auto">{{ booking.pendingCancellationCharge ? 'Card details required' : booking.cancellation?.paymentMethodId ? 'Card on File' : 'No Card on File' }}</small>
                        @if (booking.cancellation?.charges?.length) {
                            <strong class="text-nowrap">{{ null | currencyUnit:booking.cancellation!.amountHeld():booking.cancellation!.currencyCode }} paid</strong>
                        }
                    </button>
                </h6>
                <div ngbAccordionCollapse>
                    <div ngbAccordionBody class="p-3 border-top">
                        <ng-template>
                            <div class="vstack gap-3">
                                @if (booking.pendingCancellationCharge; as pending) {
                                    <span data-testid="pending-cancellation-charge-amount">Cancellation fee {{ null | currencyUnit:pending.amountForPartySize(booking.size):paymentSummary.currencyCode }}
                                        @if (pending.isPerCover) { ({{ null | currencyUnit:pending.unitAmount:paymentSummary.currencyCode }} x {{ booking.size }}) }
                                    </span>
                                    <small class="text-body-secondary">Customer {{ venue.cancellationChargeAutomatically ? 'will' : 'can' }} be charged if they cancel less than {{ venue.cancellationChargeCutOffHours }} hours before the start time.</small>
                                    <ng-container [ngTemplateOutlet]="pendingPaymentLink" [ngTemplateOutletContext]="{ card: true }"></ng-container>
                                    <div class="d-flex flex-wrap gap-2">
                                        <button type="button" class="btn btn-sm btn-outline-secondary" (click)="editPendingPayment()">Edit Cancellation Charge</button>
                                        @if (booking.status().canWaivePendingPayment()) { <button type="button" class="btn btn-sm btn-outline-secondary" (click)="waivePendingPayment()">Waive Pending Payment</button> }
                                    </div>
                                }
                                @if (booking.cancellation) {
                                    <ng-container [ngTemplateOutlet]="cancellationDetails" [ngTemplateOutletContext]="{ booking: booking }"></ng-container>
                                }
                                <div class="d-flex flex-wrap gap-2">
                                    @if (canPartiallyChargeCoversForBookedBooking()) {
                                        <button class="btn btn-sm btn-info" (click)="chargePartialClicked()">Charge for Partial No-Shows</button>
                                    } @else if (canChargeFullAmountForCancelledBooking()) {
                                        <button class="btn btn-sm btn-info" (click)="chargeCancellationFeeClicked(confirmChargeCancellationFeeModalContent)">Charge Customer</button>
                                    }
                                    @if (canRefundCancellationCharge()) {
                                        <button class="btn btn-sm btn-outline-secondary" [disabled]="isRefundingCancellation" (click)="refundCancellationChargeClicked()">Refund Cancellation Charge</button>
                                    }
                                </div>
                            </div>
                        </ng-template>
                    </div>
                </div>
            </section>
        }

        <section ngbAccordionItem #history="ngbAccordionItem" class="border rounded-3 overflow-hidden bg-body" data-testid="payment-history">
            <h6 ngbAccordionHeader class="m-0">
                <button ngbAccordionToggle type="button" class="btn border-0 rounded-0 text-start p-3 d-flex align-items-center gap-3 w-100" data-testid="payment-history-toggle">
                    <i class="bi" [class.bi-chevron-right]="history.collapsed" [class.bi-chevron-down]="!history.collapsed" aria-hidden="true"></i>
                    <span class="fw-semibold fs-6">Payment history</span>
                    <small class="text-body-secondary">{{ paymentSummary.payments.length }} {{ paymentSummary.payments.length === 1 ? 'payment' : 'payments' }}</small>
                </button>
            </h6>
            <div ngbAccordionCollapse>
                <div ngbAccordionBody class="p-3 border-top">
                    <ng-template>
                        <ul class="list-group list-group-flush">
                            @for (payment of paymentSummary.payments; track payment.id) {
                                <li class="list-group-item px-0" data-testid="payment-history-entry">
                                    <div class="d-flex justify-content-between gap-3">
                                        <div>
                                            <span class="fw-semibold">{{ payment.descriptions.join(' + ') }}</span>
                                            <small class="d-block text-body-secondary">{{ payment.paymentIntentId ? 'Online' : 'In person' }} · {{ payment.dateCreated | dateTime:venue.timeZone:DateTime.DATETIME_SHORT }}</small>
                                        </div>
                                        <div class="text-end text-nowrap">
                                            <span>{{ null | currencyUnit:payment.amount:payment.currencyCode }} paid</span>
                                            @if (payment.amountRefunded > 0) { <small class="d-block text-danger">{{ null | currencyUnit:payment.amountRefunded:payment.currencyCode }} refunded</small> }
                                        </div>
                                    </div>
                                </li>
                            } @empty { <li class="list-group-item border-0 p-0 text-body-secondary">No payments yet</li> }
                            @for (refund of booking.sale?.payment?.refunds ?? []; track refund.id) {
                                <li class="list-group-item px-0 d-flex justify-content-between gap-3">
                                    <div>Product refund<small class="d-block text-body-secondary">{{ refund.dateCreated | dateTime:venue.timeZone:DateTime.DATETIME_SHORT }}</small></div>
                                    <span class="text-danger text-nowrap">{{ null | currencyUnit:refund.amount:paymentSummary.currencyCode }}</span>
                                </li>
                            }
                        </ul>
                    </ng-template>
                </div>
            </div>
        </section>
    </div>
    @if (noPaymentsInvolved && !booking.order) {
        <p class="text-body-secondary m-0">No deposit or cancellation charge required. Use Payment actions to add one.</p>
    }
</div>

<ng-template #pendingPaymentLink let-card="card">
    <div class="vstack gap-2" data-testid="pending-payment-link">
        @if (booking.expiryDate(); as expiry) { <small class="text-body-secondary">Booking will be cancelled if the link expires at {{ expiry | dateTime:venue.timeZone:DateTime.DATETIME_SHORT }}</small> }
        <div class="d-flex flex-wrap gap-2">
            <button type="button" class="btn btn-primary" (click)="resendPendingPaymentRequested(resendPendingPaymentModalContent)"><i class="bi bi-send me-1" aria-hidden="true"></i> {{ card ? 'Send card link' : 'Send payment link' }}</button>
            <button type="button" class="btn btn-outline-secondary" (click)="copyPendingPaymentLinkToClipboard()"><i class="bi bi-link-45deg me-1" aria-hidden="true"></i> Copy link</button>
        </div>
    </div>
</ng-template>

<ng-template
    #cancellationDetails
    let-booking="booking"
>
<div class="vstack gap-2">
    <span
        class="fs-6"
        data-testid="cancellation-charge-amount"
    >
        @let charge = booking.cancellation.charge();
        @let amount = charge.amountForPartySize(booking.size);
        <span class="fw-semibold">
            {{ null | currencyUnit : amount : 'GBP' }}
        </span>
        @if (charge.isPerCover) {
            ({{ null | currencyUnit : charge.unitAmount : 'GBP' }}
            x
            {{ booking.size }})
        }
    </span>
    <hr class="my-1" />
    <div class="vstack">
        <div class="hstack gap-1">
            @if (booking.canChargeCancellationFee()) {
                <div class="hstack gap-2">
                    <div
                        class="rounded bg-warning top-0 hstack justify-content-center"
                        [style.width.px]="20"
                        [style.height.px]="20"
                    >
                        <img
                            ngSrc="assets/credit-card/normal_white.svg"
                            width="12"
                            height="12"
                            alt="Notification"
                        />
                    </div>
                    <span class="text-body-secondary">
                        @switch (booking.statusType) {
                            @case (BookingStatusType.Cancelled) {
                                Cancelled
                            }
                            @case (BookingStatusType.NoShow) {
                                No Show-ed
                            }
                            @default {
                                Cancelled
                            }
                        }
                        on
                        <span class="fw-semibold">
                            {{ booking.status().dateTime | dateTime : venue.timeZone : DateTime.DATETIME_SHORT }},
                        </span>
                        after the cut-off time of
                        <span class="fw-semibold">
                            {{ booking.cancellationCutOffDate() | dateTime : venue.timeZone : DateTime.DATETIME_SHORT }}
                        </span>
                    </span>
                </div>
            } @else {
                @if (booking.cancellation.chargeAutomatically) {
                    <span>
                        Customer will be charged if they cancel after
                    </span>
                } @else {
                    <span>
                        Customer can be charged if they cancel after
                    </span>
                }
                <span class="fw-semibold">
                    {{ booking.cancellationCutOffDate() | dateTime : venue.timeZone : DateTime.DATETIME_SHORT }}
                </span>
            }
        </div>
    </div>
    @if (booking.canRemoveCancellation()) {
        <hr class="my-1" />
        <button
            class="btn btn-outline-danger align-self-start"
            [disabled]="isRemovingCancellation"
            (click)="removeCancellation()"
        >
            Remove Cancellation
        </button>
    }
</div>
</ng-template>

<ng-template #confirmRemoveCancellationModal>
    <div class="vstack gap-2">
        @if (removalFailureMessage) {
            <div class="alert alert-danger py-2 px-3 mb-0" role="alert">
                {{ removalFailureMessage }}
            </div>
        }
        <p class="m-0">This booking will stay Booked.</p>
        <p class="m-0">The cancellation will be removed and these saved card details will no longer be available for use through TableSense.</p>
        <p class="m-0">We will also request deletion of the saved card from your payment provider.</p>
    </div>
</ng-template>

<ng-template #waivePendingPaymentModal>
    <div class="vstack gap-1">
    <span>
        @if (booking.pendingDepositCharge; as charge) {
            The customer has not yet paid the
            <span>
                {{ null | currencyUnit: charge.amount : 'GBP' }}
            </span>
            deposit charge.
        }
        @if (booking.pendingCancellationCharge; as charge) {
            The customer has not yet saved their card details for the
            <span>
                {{ null | currencyUnit: charge.amount : 'GBP' }}
            </span>
            cancellation charge.
        }
    </span>
        Are you sure you want to waive this?
        <p class="fw-semibold m-0">
            This action cannot be undone.
        </p>
    </div>
</ng-template>

<ng-template
    #confirmChargeCancellationFeeModalContent
>
    @if (booking.activeCancellationChargeForBooking()) {
        <ng-container
            [ngTemplateOutlet]="confirmChargeCancellationFeeModalBody"
            [ngTemplateOutletContext]="{ charge: booking.activeCancellationChargeForBooking() }"
        >
        </ng-container>
    }
</ng-template>

<ng-template
    #confirmChargeCancellationFeeModalBody
    let-charge="charge"
>
    @if (chargeFailureMessage) {
        <div class="alert alert-danger py-2 px-3 mb-2" role="alert">
            {{ chargeFailureMessage }}
        </div>
    }
    <span class="p-2">
        Are you sure you like to charge the
        <span class="fw-bold">
            {{ null | currencyUnit: charge.unitAmount : 'GBP' }}
            <ng-container
                *ngIf="charge.isPerCover"
            >
                per person
            </ng-container>
        </span>
        cancellation fee?
    </span>
</ng-template>

<ng-template #confirmRefundPaymentModal>
    @if (paymentToRefund; as payment) {
        <div class="vstack gap-3">
            <span>
                @if (payment.paymentIntentId !== null) {
                    Refund up to
                } @else {
                    Mark up to
                }
                <span class="fw-bold">
                    {{ null | currencyUnit: payment.amountRefundable() : payment.currencyCode }}
                </span>
                @if (payment.paymentIntentId !== null) {
                    to the customer?
                } @else {
                    of this in-person payment as refunded?
                }
            </span>
            @if (payment.paymentIntentId === null) {
                <div class="alert alert-info py-2 px-3 mb-0">
                    No money will be moved by TableSense.
                </div>
            }
            <div>
                <label
                    for="refundPaymentAmount"
                    class="form-label"
                >
                    Amount to refund
                </label>
                <div class="input-group">
                    <span class="input-group-text">
                        {{ payment.currencyCode }}
                    </span>
                    <input
                        id="refundPaymentAmount"
                        name="refundPaymentAmount"
                        class="form-control"
                        type="number"
                        min="0.01"
                        step="0.01"
                        [max]="payment.amountRefundable() / 100"
                        [attr.aria-invalid]="refundPaymentError ? 'true' : null"
                        [attr.aria-describedby]="refundPaymentError ? 'refundPaymentError' : null"
                        [(ngModel)]="refundPaymentAmount"
                    >
                </div>
                @if (refundPaymentError) {
                    <div
                        id="refundPaymentError"
                        class="text-danger form-text"
                        role="alert"
                    >
                        {{ refundPaymentError }}
                    </div>
                }
            </div>
            <p class="fw-semibold m-0">
                This action cannot be undone.
            </p>
        </div>
    }
</ng-template>

<ng-template #confirmRefundCancellationModal>
    <div class="vstack gap-1">
        <span>
            Are you sure you want to refund the cancellation charge of
            <span class="fw-bold">
                @let cancellation = booking.cancellation;
                @if (cancellation) {
                    @let charge = cancellation.charges[0];
                    @let amount = charge.charge().amount;
                    {{ null | currencyUnit : amount : booking.cancellation?.currencyCode }}
                }
            </span>
            to the customer?
        </span>
        <p class="fw-semibold m-0">
            This action cannot be undone.
        </p>
    </div>
</ng-template>

<ng-template #confirmPartialChargeModal>
    <div class="vstack gap-3">
    @if (chargeFailureMessage) {
        <div class="alert alert-danger py-2 px-3 mb-0" role="alert">
                {{ chargeFailureMessage }}
            </div>
        }
        <div class="vstack gap-2">
            <span class="text-body-secondary">
                Select the number of covers to charge if some guests are not attending.
                The customer will be notified of this charge.
            </span>
            <div class="hstack gap-2 justify-content-center py-2">
                <app-number-input
                    [minimum]="1"
                    [maximum]="maxPartialChargeCovers()"
                    [iconPath]="'assets/people/normal.svg'"
                    [pluralMapping]="coversPluralMapping"
                    [ngModel]="partialChargeCovers"
                    (ngModelChange)="onPartialChargeCoversChange($event)"
                >
                </app-number-input>
                <span class="text-body-secondary">
                    of {{ remainingCoversToCharge() }} uncharged covers
                </span>
            </div>
        </div>
        <hr class="my-0" />
        <div class="hstack gap-2 justify-content-between">
            <span>Amount to charge:</span>
            <span class="fw-bold fs-5">
                {{ null | currencyUnit : getPartialChargeAmount() : booking.cancellation?.currencyCode }}
            </span>
        </div>
    </div>
</ng-template>

<ng-template #refundProductsModal>
    @if (booking.sale; as sale) {
        <div class="vstack gap-3" data-testid="refund-products-picker">
            @if (!sale.payment?.canRefundOnline) {
                <p class="mb-0">These products were paid in person. Return the money separately before marking them as refunded.</p>
            }
            <p class="mb-0">Choose whole product lines to refund their remaining paid amount, including all quantities and modifiers. You can refund products marked non-refundable to the customer.</p>
            @for (line of sale.order.lineItems; track line.id) {
                @if (line.paidOnline) {
                <label class="d-flex align-items-start gap-3 border rounded-3 p-3">
                    <input type="checkbox" class="form-check-input" [checked]="selectedOrderLineIds.has(line.id)"
                        [disabled]="isRefundingOrder || !sale.canRefundLine(line.id)" (change)="toggleOrderLine(line.id)" />
                    <span class="flex-grow-1">{{ line.quantity }} × {{ line.name }}
                        @for (modifier of line.modifiers; track modifier.id) { <small class="d-block text-body-secondary">{{ modifier.name }}</small> }
                        <small class="d-block text-body-secondary">
                            @if (sale.isLineRefunded(line.id)) {
                                Already refunded
                            } @else if (line.customerRefundableUntil) {
                                Customer can refund if they cancel before {{ line.customerRefundableUntil | date:'short' }}
                            } @else {
                                Customer cannot refund
                            }
                        </small>
                    </span>
                    <strong>{{ null | currencyUnit:sale.remainingLineAmount(line.id):sale.order.currencyCode }}</strong>
                </label>
                }
            }
            <div class="d-flex justify-content-between border-top pt-3"><span>Refund total</span><strong>{{ null | currencyUnit:orderRefundTotal:sale.order.currencyCode }}</strong></div>
            @if (orderRefundError) { <p role="alert" class="text-danger mb-0">{{ orderRefundError }}</p> }
        </div>
    }
</ng-template>

<ng-template
    #resendPendingPaymentModalContent
>
    <div class="vstack gap-2">
        <span>
            How would you like to resend the link?
        </span>
        <label class="">
            Send to:
        </label>
        <div class="vstack gap-2 align-content-stretch">
            <label
                class="btn btn-outline-secondary p-2 d-flex flex-column justify-content-center align-items-center"
                [class.disabled]="!booking.emailAddress"
            >
                <div class="m-1 hstack gap-2">
                    <input
                        type="radio"
                        [value]="'email'"
                        [disabled]="!booking.emailAddress"
                        [checked]="pendingPaymentDeliveryMethod.value === 'email'"
                        (change)="pendingPaymentDeliveryMethod.setValue('email')"
                        class="form-check-input m-0"
                        role="button"
                    >
                    <div class="vstack align-items-start">
                        <span
                            *ngIf="booking.emailAddress"
                            class="fw-semibold"
                        >
                            Email: {{ booking.emailAddress }}
                        </span>
                        <span
                            *ngIf="!booking.emailAddress"
                            class="fw-normal"
                        >
                            Email: Email address not provided
                        </span>
                    </div>
                </div>
            </label>
            <label
                class="btn btn-outline-secondary p-2 d-flex flex-column justify-content-center align-items-center"
                [class.disabled]="!booking.phoneNumber"
            >
                <div class="m-1 hstack gap-2">
                    <input
                        type="radio"
                        [value]="'sms'"
                        [disabled]="!booking.phoneNumber"
                        [checked]="pendingPaymentDeliveryMethod.value === 'sms'"
                        (change)="pendingPaymentDeliveryMethod.setValue('sms')"
                        class="form-check-input m-0"
                        role="button"
                    >
                    <div class="vstack align-items-start">
                        <span
                            *ngIf="booking.phoneNumber"
                            class="fw-semibold"
                        >
                            SMS: {{ booking.phoneNumber | phoneNumber }}
                        </span>
                        <span
                            *ngIf="!booking.phoneNumber"
                            class="fw-normal"
                        >
                            SMS: Phone number not provided
                        </span>
                    </div>
                </div>
            </label>
        </div>
    </div>
</ng-template>
